// src/hooks/usePWAStatus.ts
import { useEffect, useMemo, useState } from "react";

type OS =
    | "android"
    | "ios"
    | "windows"
    | "mac"
    | "linux"
    | "chromeos"
    | "unknown";

function detectOS(): OS {
    // Prefer User-Agent Client Hints if available
    const anyNav = typeof navigator !== "undefined" ? (navigator as any) : undefined;
    const uaData = anyNav?.userAgentData;
    const platformCH: string | undefined = uaData?.platform;

    const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
    const plat = typeof navigator !== "undefined" ? (navigator as any).platform : "";

    // iPadOS often reports as "Mac" but with touch points
    const isIPadOS =
        /Mac/i.test(platformCH || plat || "") &&
        typeof navigator !== "undefined" &&
        (navigator as any).maxTouchPoints > 1;

    // iOS detection
    const isiOSClassic = /iPhone|iPad|iPod/i.test(ua);
    if (isIPadOS || isiOSClassic) return "ios";

    // Android
    if (/Android/i.test(platformCH || ua)) return "android";

    // Windows
    if (/Win/i.test(platformCH || plat || ua)) return "windows";

    // ChromeOS (Client Hints often says "Chrome OS")
    if (/(CrOS|Chrome OS)/i.test(platformCH || ua)) return "chromeos";

    // macOS
    if (/Mac/i.test(platformCH || plat || ua)) return "mac";

    // Linux
    if (/Linux/i.test(platformCH || plat || ua)) return "linux";

    return "unknown";
}

export function usePWAStatus() {
    const [isStandalone, setStandalone] = useState(false);
    const [installedOnce, setInstalledOnce] = useState<boolean>(() => {
        // Guard SSR
        if (typeof window === "undefined") return false;
        try {
            return localStorage.getItem("pwa-installed") === "1";
        } catch {
            return false;
        }
    });

    const [os, setOS] = useState<OS>(() => detectOS());

    useEffect(() => {
        if (typeof window === "undefined") return;

        const byUserAgent =
            navigator.userAgent.includes("RinvoxApp/1.0") ||
            navigator.userAgent.includes("MAUIWebView");

        const byWindowFlag =
            (window as any).__RINVOX_NATIVE_APP__ === true ||
            (window as any).__RINVOX_INSTALLED_APP__ === true;

        const byStorage =
            localStorage.getItem("rinvox_native_app") === "1" ||
            localStorage.getItem("rinvox_installed_app") === "1";

        // --- display-mode:standalone watcher
        const checkStandalone = () => {
            const standalone =
                (window.matchMedia?.("(display-mode: standalone)").matches ?? false) ||
                // legacy iOS A2HS
                ((navigator as any).standalone === true) ||
                // by muai app
                byUserAgent || byWindowFlag || byStorage ||
                // Android when launched from app shortcut
                document.referrer.startsWith("android-app://");
            setStandalone(standalone);
        };

        checkStandalone();

        const mq = window.matchMedia?.("(display-mode: standalone)");
        const onChange = () => checkStandalone();
        mq?.addEventListener?.("change", onChange);

        // --- appinstalled event
        const onInstalled = () => {
            setInstalledOnce(true);
            try {
                localStorage.setItem("pwa-installed", "1");
            } catch { }
            checkStandalone();
        };
        window.addEventListener("appinstalled", onInstalled);

        // --- OS (re)detect on visibility change (some WebViews update hints late)
        const reDetect = () => setOS(detectOS());
        document.addEventListener("visibilitychange", reDetect);

        return () => {
            mq?.removeEventListener?.("change", onChange);
            window.removeEventListener("appinstalled", onInstalled);
            document.removeEventListener("visibilitychange", reDetect);
        };
    }, []);

    const isInstalled = isStandalone || installedOnce;

    // Convenience booleans
    const flags = useMemo(() => {
        const isAndroid = os === "android";
        const isIOS = os === "ios";
        const isWindows = os === "windows";
        const isMac = os === "mac";
        const isLinux = os === "linux";
        const isChromeOS = os === "chromeos";

        // Rough mobile/tablet/desktop hint
        const isMobile = isAndroid || isIOS;
        const isDesktop = isWindows || isMac || isLinux || isChromeOS;

        return {
            os,
            isAndroid,
            isIOS,
            isWindows,
            isMac,
            isLinux,
            isChromeOS,
            isMobile,
            isDesktop,
        };
    }, [os]);

    return {
        // PWA status
        isStandalone,
        isInstalled,

        // Platform info
        ...flags,
    };
}
